import { createSelector } from "@reduxjs/toolkit"
import { RootState } from "store/store"
import Big from "big.js"
import { CENT, LimitStepEnum, NO_ACTIVE_ORDER, ORDER_LINE, UNDERSCORE } from "../constants"
import { isNotEmpty } from "utils"
import { Product, BodyPostOrder } from "../types"
import { format } from "date-fns"
import { BC_CGV } from "utils/constants"
import { isStoreChainAuthorized } from "utils/bikeLabel"
import i18next from "i18next"

//Calcul TTC Total
export const getTotalPriceTTCSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.products,
	(products) => {
		// Set the decimal precision to 2 places, ensuring all calculations with Big.js are rounded to two decimal points.
		Big.DP = 2
		// Configure the rounding mode to 'roundHalfUp', where numbers halfway between two integers are rounded towards the nearest higher integer.
		Big.RM = Big.roundHalfUp
		const { totalTTC, totalHTC, quantity, tvaTotal } = products.reduce(
			(previous, current) => {
				const tva = new Big(current.prices.vat_rate / CENT)
				const productPriceHT = new Big(current.prices.price_excl_vat)
				const quantity = new Big(current.quantity)
				const tvaProduct = productPriceHT.mul(tva).mul(quantity)
				const lineTotalHT = productPriceHT.mul(quantity)
				const lineTotalTTC = lineTotalHT.mul(tva.plus(1))
				return {
					quantity: previous.quantity.plus(quantity),
					totalTTC: previous.totalTTC.plus(lineTotalTTC),
					totalHTC: previous.totalHTC.plus(lineTotalHT),
					tvaTotal: previous.tvaTotal.plus(tvaProduct),
				}
			},
			{
				totalHTC: new Big(0),
				totalTTC: new Big(0),
				quantity: new Big(0),
				tvaTotal: new Big(0),
			}
		)
		return [
			{
				total: totalHTC.toFixed(2),
				quantity: quantity.toFixed(0),
				label: i18next.t("order.totalHt", { ns: "label" }),
			},
			{
				total: tvaTotal.toFixed(2),
				quantity: 0,
				label: i18next.t("order.tva", { ns: "label" }),
			},
			{
				label: i18next.t("order.totalTTC", { ns: "label" }),
				total: totalTTC.toFixed(2),
				quantity: 0,
			},
		]
	}
)

//Get ProductsList
export const getProductsSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.products,
	(products) => {
		return products.map((product) => {
			const price = new Big(product.prices.price_excl_vat)
			const quantity = new Big(product?.quantity ?? 0)
			return {
				...product,
				total: price.eq(0) ? UNDERSCORE : price.mul(quantity).toFixed(2),
			}
		})
	}
)

//Validate State
export const activeOrderSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.validatedLabelOrderStep,
	(state) => state !== NO_ACTIVE_ORDER && state < String(LimitStepEnum.MAX_LIMIT_STEP)
)

export const getQuantitySelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.products,
	(products) => {
		const quantity = products.reduce((previous, product) => {
			return previous + product.quantity
		}, 0)
		return {
			quantity: quantity,
			hasQuantity: quantity > 0,
		}
	}
)

export const getBilingInformationSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder,
	({
		invoicing_agent_firstname,
		invoicing_agent_lastname,
		invoicing_agent_mail,
		sepaInProgress,
		errorsBilingForm,
	}) => {
		return {
			invoicing_agent_firstname,
			invoicing_agent_lastname,
			invoicing_agent_mail,
			sepaInProgress,
			errorsBilingForm,
		}
	}
)

export const getBilingFormInformationSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder,
	({ invoicing_agent_firstname, invoicing_agent_lastname, invoicing_agent_mail }) => ({
		invoicing_agent_firstname,
		invoicing_agent_lastname,
		invoicing_agent_mail,
	})
)

export const isAuthorizedToCommandSelector = createSelector(
	[
		(state: RootState) => state.bikeLabelOrder,
		(state, chain) => chain,
		(state, chain, isBicyOperator: boolean) => isBicyOperator,
	],
	(state, chain, isBicyOperator) => {
		const hasQuantity =
			state.products.reduce((previous, product) => {
				return previous + product.quantity
			}, 0) > 0
		const hasInformation =
			isNotEmpty(state.invoicing_agent_firstname) &&
			isNotEmpty(state.invoicing_agent_lastname) &&
			isNotEmpty(state.invoicing_agent_mail)
		const has_good_chain = isStoreChainAuthorized(chain)
		return hasQuantity && hasInformation && has_good_chain && isBicyOperator
	}
)

export const formBilingInformationReadySelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.errorsBilingForm,
	(state) => !isNotEmpty(state)
)

export const disabledFormInformationSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.sepaInProgress,
	(state) => state
)

export const orderFormatBodyPostSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.products,
	(products: Product[]) => {
		return products.reduce<BodyPostOrder>(
			(acc, curr) => {
				if (curr.quantity > 0) {
					acc.order_lines = [
						...acc.order_lines,
						{ product_id: curr.product_id, quantity: curr.quantity },
					]
				}
				return acc
			},
			{
				order_lines: [],
				order_data: {
					cgv_version: BC_CGV,
					cgv_accepted_at: format(new Date(), "yyyy-MM-dd HH:mm:ss.SSS"),
				},
			}
		)
	}
)

//Count of Product
export const productCountSelector = createSelector(
	(state: RootState) => state.bikeLabelOrder.products,
	(state) => {
		return state?.length
	}
)
